/**
 * useSubscriptionPrices
 *
 * Fetches localized pricing from RevenueCat (via subscriptionService) for the
 * four product identifiers we sell.  Returns price strings as reported by the
 * App Store / Play Store so they are always in the user's local currency.
 *
 * Returns null for any price that cannot be resolved from RevenueCat.
 * Callers should treat null as "price unavailable" and show a placeholder (—)
 * rather than a hardcoded USD amount.
 *
 * Initialization: RevenueCat must be initialized before getOfferings() works.
 * This hook calls initialize() using the authenticated user's ID once available,
 * then fetches prices.  Until the user ID is known, isLoaded remains false so
 * callers can show a loading state rather than stale — values.
 */

import { useEffect, useState } from 'react';
import { Platform } from 'react-native';
import { subscriptionService } from '@/services/subscriptionService';
import { getProductIdForPlatform } from '@/config/subscriptionTiers';
import { useAuth } from '@/contexts/AuthContext';

export interface SubscriptionPrices {
  basicMonthly: string | null;
  basicAnnual: string | null;
  fullMonthly: string | null;
  fullAnnual: string | null;
  extendedMonthly: string | null;
  extendedAnnual: string | null;
  isLoaded: boolean;
}

const NULL_PRICES: SubscriptionPrices = {
  basicMonthly: null,
  basicAnnual: null,
  fullMonthly: null,
  fullAnnual: null,
  extendedMonthly: null,
  extendedAnnual: null,
  isLoaded: true,
};

export function useSubscriptionPrices(): SubscriptionPrices {
  const { user } = useAuth();

  const [prices, setPrices] = useState<SubscriptionPrices>({
    basicMonthly: null,
    basicAnnual: null,
    fullMonthly: null,
    fullAnnual: null,
    extendedMonthly: null,
    extendedAnnual: null,
    isLoaded: false,
  });

  useEffect(() => {
    // Web builds cannot use RevenueCat — prices remain null (no IAP on web)
    if (Platform.OS === 'web') {
      setPrices({ ...NULL_PRICES });
      return;
    }

    // Don't attempt a fetch until we have a userId.
    // RevenueCat MUST be initialized with a userId before getOfferings() works.
    // Keeping isLoaded=false here causes callers to show a proper loading state
    // (e.g. SubscriptionDrawer won't render UpgradeComparisonCard yet) rather
    // than showing stale — values.
    if (!user?.id) {
      return;
    }

    let cancelled = false;

    async function fetchPrices() {
      try {
        // Ensure RevenueCat is initialized.  This is a no-op if already done.
        await subscriptionService.initialize(user!.id);

        const packages = await subscriptionService.getOfferings();

        if (cancelled) return;

        if (packages.length === 0) {
          console.warn('⚠️ useSubscriptionPrices: RevenueCat returned no packages');
          setPrices({ ...NULL_PRICES });
          return;
        }

        // Build a productId → priceString lookup
        const priceMap: Record<string, string> = {};
        packages.forEach((pkg) => {
          const productId = pkg.product.identifier;
          priceMap[productId] = pkg.product.priceString;
        });

        const resolve = (tierId: 'basic' | 'full' | 'extended', period: 'monthly' | 'annual'): string | null => {
          const id = getProductIdForPlatform(tierId, period, Platform.OS);
          return id ? priceMap[id] ?? null : null;
        };

        setPrices({
          basicMonthly: resolve('basic', 'monthly'),
          basicAnnual: resolve('basic', 'annual'),
          fullMonthly: resolve('full', 'monthly'),
          fullAnnual: resolve('full', 'annual'),
          extendedMonthly: resolve('extended', 'monthly'),
          extendedAnnual: resolve('extended', 'annual'),
          isLoaded: true,
        });
      } catch (err) {
        console.warn('⚠️ useSubscriptionPrices: could not fetch prices', err);
        if (!cancelled) {
          setPrices({ ...NULL_PRICES });
        }
      }
    }

    fetchPrices();
    return () => {
      cancelled = true;
    };
  }, [user?.id]); // Re-run when user ID becomes available (e.g. after auth resolves)

  return prices;
}
